import React, { FC, useEffect, useMemo } from 'react';

import { Box, HStack, Radio, RadioGroup, Stack } from '@chakra-ui/react';
import { find } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { INIT_PAGE_INFO } from 'services/utils/constants';
import { customTheme } from 'theme';

import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import {
  ButtonGroup,
  Select,
  SelectHistoricalOptions,
} from 'components/DataEntry';
import { ActiveResourceProvider } from 'containers/ActiveCloudResource/hooks/context/ActiveResourceProvider';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import {
  viewVulnerabilityByItems,
  vulnerabilityResourceTypeList,
} from 'containers/Visibility/Vulnerabilities/Components/utils';
import { CVETable } from 'containers/Visibility/Vulnerabilities/Components/VulnerabilityTable/CVE/CVETable';
import { PackageVulnerabilities } from 'containers/Visibility/Vulnerabilities/Components/VulnerabilityTable/Package/PackageVulnerabilities';
import { ResourceVulnerabilities } from 'containers/Visibility/Vulnerabilities/Components/VulnerabilityTable/ResourceVulnerabilities';
import { VulnerabilityHeaderFilters } from 'containers/Visibility/Vulnerabilities/Components/VulnerabilityTable/VulnerabilityHeaderFilters';
import { selectVulnerabilities } from 'containers/Visibility/Vulnerabilities/selectors';
import { VulnerabilityViewByTabs } from 'containers/Visibility/Vulnerabilities/types';
import { useSearchParams } from 'hooks/useSearchParams';

import { actions } from '../../slice';

interface Props {
  isExpanded?: boolean;
  setIsExpanded?(val: boolean);
}

export const VulnerabilityTabs: FC<Props> = props => {
  const dispatch = useDispatch();
  const { vulnerabilityType, resourceType, publishedAt } = useSelector(
    selectVulnerabilities,
  );
  const [viewBy, setViewBy] = React.useState(viewVulnerabilityByItems[0]);
  const params = useSearchParams<{ tab: string }>();
  const { updateValueInURL } = useSearchParamFunctions();

  useEffect(() => {
    if (!!params.tab) {
      setViewBy(
        params.tab === VulnerabilityViewByTabs.CVE
          ? viewVulnerabilityByItems[1]
          : params.tab === VulnerabilityViewByTabs.Package
          ? viewVulnerabilityByItems[2]
          : viewVulnerabilityByItems[0],
      );
      if (
        params.tab !== VulnerabilityViewByTabs.CVE &&
        params.tab !== VulnerabilityViewByTabs.Package
      ) {
        if (params.tab !== VulnerabilityViewByTabs.Resource)
          dispatch(actions.updateResourceType(params.tab));
        dispatch(actions.updateVulnerabilityPageInfo(INIT_PAGE_INFO));
      }
    }
  }, [params.tab, dispatch]);

  /*//set type from selected tab
  useEffect(() => {
    const option = find(
      [
        ...vulnerabilityTypeList[0].options,
        ...vulnerabilityTypeList[1].options,
      ],
      o => o.value === selectedTab,
    );
    setType(option!);
  }, [selectedTab]);*/

  const vulnerabilityTypeItems = useMemo(
    () => [
      {
        label: 'Open',
        value: 'open',
        isDisabled: viewBy.value === VulnerabilityViewByTabs.CVE,
      },
      {
        label: 'Resolved',
        value: 'resolved',
        isDisabled: viewBy.value === VulnerabilityViewByTabs.CVE,
      },
    ],
    [viewBy.value],
  );

  return (
    <Stack spacing={4} h="full">
      <HStack pl={3}>
        <FilterLabelWrapper>VIEW BY</FilterLabelWrapper>
        <ButtonGroup
          items={viewVulnerabilityByItems}
          value={viewBy}
          onClick={item => {
            updateValueInURL('tab', item.value);
            if (item.value === VulnerabilityViewByTabs.Resource)
              dispatch(actions.updateVulnerabilityPageInfo(INIT_PAGE_INFO));
          }}
        />
        <Box pl={4} color={customTheme.colors.gray['300']}>
          By Resource Type
        </Box>
        <Box>
          <Select
            options={vulnerabilityResourceTypeList}
            value={
              find(
                vulnerabilityResourceTypeList[0].options,
                o => o.value === resourceType,
              ) || vulnerabilityResourceTypeList[0].options[0]
            }
            isDisabled={viewBy.value !== VulnerabilityViewByTabs.Resource}
            onChange={option => {
              updateValueInURL('tab', option.value);
              dispatch(actions.updateVulnerabilityPageInfo(INIT_PAGE_INFO));
              //dispatch(actions.updateResourceType(option?.value));
            }}
            showClearIndicator={false}
            showIconInValueContainer
            /*styles={{
              menu: provided => ({
                ...defaultSelectStyles().menu(provided),
                maxHeight: '100%',
              }),
            }}*/
          />
        </Box>
        <Box pl={4} pr={2}>
          <FilterLabelWrapper>LIST</FilterLabelWrapper>
        </Box>
        <RadioGroup
          value={vulnerabilityType}
          onChange={val => dispatch(actions.updateVulnerabilityType(val))}
        >
          <HStack>
            {map(vulnerabilityTypeItems, (o, index) => (
              <Radio
                value={o.value}
                isDisabled={o.isDisabled}
                key={index + o.value}
              >
                {o.label}
              </Radio>
            ))}
          </HStack>
        </RadioGroup>
      </HStack>
      <BorderedBox w="full" h="full" p={4}>
        <Stack flex={1} w="full" h="full" spacing={5}>
          <HStack>
            <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
            <VulnerabilityHeaderFilters />
            <Box>
              <SelectHistoricalOptions
                historicalTime={publishedAt}
                setHistoricalTime={s => dispatch(actions.updatePublishedAt(s))}
                leftMessage="Timeline"
                isAFilter
                width="full"
              />
            </Box>
          </HStack>
          <Box h="full">
            <ActiveResourceProvider>
              {params.tab === VulnerabilityViewByTabs.CVE ? (
                <CVETable />
              ) : params.tab === VulnerabilityViewByTabs.Package ? (
                <PackageVulnerabilities />
              ) : (
                <ResourceVulnerabilities />
              )}
            </ActiveResourceProvider>
          </Box>
        </Stack>
      </BorderedBox>
    </Stack>
  );
};
